import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormikErrors, FormikTouched } from 'formik';
import { Form, Select } from 'semantic-ui-react';
import styled from 'styled-components';

import { useScrollWhenError, useUnloadDialogWithFormik } from '@/hooks/formik';
import { BILLING_NOTE_LENGTH } from '@/constants';
import {
  SubscriptionRequest,
  SubscriptionRequestErrors,
} from '@/types/subscriptions/request';
import { User } from '@/types/user';
import { Contact } from '@/types/contact';
import { Field } from '../../Container/Form/Field';
import { UpperLimitUser } from '../Form/UpperLimitUser';
import { ContactModal } from '../Form/ContactModal';
import { BillingContactModal } from '../Form/BillingContactModal';
import { BillingContactHint } from '../../Container/Form/Field/BillingContactHint';
import { BillingNoteHint } from '../../Container/Form/Field/BillingNoteHint';

// 独自エラーのType
type CustomErrorValues = { upperLimitUser?: undefined };
export type Values = SubscriptionRequest & CustomErrorValues;

type Props = {
  serviceGroupId: number;
  defaultBillingContactId?: number;
  defaultContactId?: number;
  extraErrors: SubscriptionRequestErrors;
  formikErrors: FormikErrors<Values>;
  isSubmitting: boolean;
  touched: FormikTouched<Values>;
  values: Values;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<Values>>;
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<Values>>;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
};

export const FormContents: React.FC<Props> = ({
  serviceGroupId,
  defaultBillingContactId,
  defaultContactId,
  extraErrors,
  formikErrors,
  isSubmitting,
  touched,
  values,
  setFieldValue,
  setFieldTouched,
  handleSubmit,
}) => {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isBillingContactOpen, setIsBillingContactOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState<'new' | 'edit'>(
    'new',
  );
  const [billingContactmodalMode, setBillingContactModalMode] = useState<
    'new' | 'edit'
  >('new');
  const [contactToEdit, setContactToEdit] = useState<Contact>();
  const [billingContactToEdit, setBillingContactToEdit] = useState<Contact>();

  // combine validation errors and api errors
  const errors = useMemo(
    () => ({ ...formikErrors, ...extraErrors }),
    [formikErrors, extraErrors],
  ) as typeof formikErrors;

  const { t } = useTranslation();

  useScrollWhenError(isSubmitting, errors);
  useUnloadDialogWithFormik(touched);

  useEffect(() => {
    setFieldValue(
      'billingContactId',
      values.billingContactId || defaultBillingContactId,
    );
    setFieldValue('contactId', values.contactId || defaultContactId);
    // https://github.com/formium/formik/issues/2059
    setTimeout(() => setFieldTouched('contactId', true));
    setTimeout(() => setFieldTouched('billingContactId', true));
  }, [
    defaultBillingContactId,
    defaultContactId,
    setFieldTouched,
    setFieldValue,
    values.billingContactId,
    values.contactId,
  ]);

  const startsOnFieldOnChanged = useCallback(
    (value: string, touch: boolean) => {
      setFieldValue('startsOn', value);
      setFieldTouched('startsOn', touch);
    },
    [setFieldTouched, setFieldValue],
  );

  const projectClassesFieldOnChanged = useCallback(
    (projectClassId: number | null) => {
      setFieldTouched('projectClassId', true);
      setFieldValue('projectClassId', projectClassId);
    },
    [setFieldTouched, setFieldValue],
  );
  return (
    <>
      <Styled>
        <Form className="subscription-form" onSubmit={handleSubmit}>
          <div className="l-block">
            <Field.ServicesField
              value={values.serviceIds}
              serviceGroupId={serviceGroupId}
              pushServiceIds={(serviceId: number) => {
                setFieldTouched('serviceIds', true);
                setFieldValue(
                  'serviceIds',
                  Array.from(new Set([...values.serviceIds, serviceId])),
                );
              }}
              pruneServiceIds={(serviceId: number) => {
                setFieldTouched('serviceIds', true);
                setFieldValue(
                  'serviceIds',
                  values.serviceIds.filter((v) => v !== serviceId),
                );
              }}
              refillServiceIds={(serviceId: number) => {
                setFieldTouched('serviceIds', true);
                setFieldValue('serviceIds', [serviceId]);
              }}
              error={
                touched.serviceIds && errors.serviceIds ? errors.serviceIds : ''
              }
            />
          </div>
          <div className="l-block">
            <Field.StartsOnField
              onChange={startsOnFieldOnChanged}
              error={touched.startsOn && errors.startsOn ? errors.startsOn : ''}
              value={values.startsOn}
              dateFormat={t('ui.locale.date')}
              serviceGroupId={serviceGroupId}
              serviceId={values.serviceIds}
            />
          </div>

          <Field.ProjectClassesField
            selected={values.projectClassId}
            onChange={projectClassesFieldOnChanged}
            error={
              touched.projectClassId && errors.projectClassId
                ? errors.projectClassId
                : ''
            }
          />
          <div className="l-block">
            <Field.AdminsField
              value={values.adminIds}
              onChange={(data, adminId) => {
                const ids = data.checked
                  ? Array.from(new Set([...values.adminIds, adminId]))
                  : values.adminIds.filter((v) => v !== adminId);
                setFieldTouched('adminIds', true);
                setFieldValue('adminIds', ids);
              }}
              error={touched.adminIds && errors.adminIds ? errors.adminIds : ''}
              serviceGroupId={serviceGroupId}
            />
          </div>
          <div className="l-block">
            <Field.UsersField.NONGLS
              onChange={(users: User[]) => {
                setFieldTouched('users', true, false);
                setFieldValue('users', users, true);
              }}
              errors={touched.users && errors.users ? errors.users : []}
              value={values.users}
            >
              <Field.UsersField.ErrorMessages
                errors={touched.users && errors.users ? errors.users : []}
              />
            </Field.UsersField.NONGLS>
            <UpperLimitUser
              error={errors.upperLimitUser}
              serviceGroupId={serviceGroupId}
            />
          </div>

          <div className="l-block">
            <div className="field">
              <ul className="hint-separated">
                <li className="item">
                  {t('models.subscription.billingContact')}
                </li>
                <li className="hint">
                  <small>
                    <BillingContactHint serviceGroupId={serviceGroupId} />
                  </small>
                </li>
              </ul>
              {!values.projectClassId ? (
                <Field.BillingContactField
                  id="billingContact"
                  value={values.billingContactId}
                  onChange={(selectedContactId, touch) => {
                    setFieldValue('billingContactId', selectedContactId);
                    setFieldTouched('billingContactId', touch);
                  }}
                  error={errors.billingContactId ? errors.billingContactId : ''}
                  setIsOpen={setIsBillingContactOpen}
                  target={'billingContactId'}
                  setModalMode={setBillingContactModalMode}
                  setContactToEdit={setBillingContactToEdit}
                />
              ) : (
                <DisabledContactField />
              )}
            </div>
          </div>

          {!values.projectClassId && (
            <>
              <label className="heading">
                {t('models.subscription.billingNote')}
                <small>
                  {t('text.upToCharacters', {
                    num: BILLING_NOTE_LENGTH,
                  })}
                </small>
                <small>
                  <BillingNoteHint serviceGroupId={serviceGroupId} />
                </small>
              </label>
              <Field.BillingNoteField
                value={values.billingNote}
                onChange={(billingNote) => {
                  setFieldValue('billingNote', billingNote);
                }}
                error={
                  touched.billingNote && errors.billingNote
                    ? errors.billingNote
                    : ''
                }
              />
            </>
          )}

          <div className="l-block">
            <div className="field">
              <label>{t('models.subscription.contact')}</label>
              {!values.projectClassId ? (
                <Field.ContactField
                  id="contact"
                  value={values.contactId}
                  onChange={(selectedContactId, touch) => {
                    setFieldValue('contactId', selectedContactId);
                    setFieldTouched('contactId', touch);
                  }}
                  error={errors.contactId ? errors.contactId : ''}
                  setIsOpen={setIsContactOpen}
                  target={'contactId'}
                  setModalMode={setContactModalMode}
                  setContactToEdit={setContactToEdit}
                />
              ) : (
                <>
                  <DisabledContactField />
                  <div className="hint">
                    <Trans i18nKey="ui.expression.addressNotSpecifiable">
                      <a
                        href="https://goo.gl/T4toCu"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        こちら
                      </a>
                    </Trans>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="next-button">
            <Form.Button
              data-test="submit"
              type="submit"
              primary={true}
              disabled={isSubmitting}
            >
              {t('ui.action.goNext')}
            </Form.Button>
          </div>
        </Form>
      </Styled>
      <ContactModal
        isOpen={isContactOpen}
        setIsOpen={setIsContactOpen}
        setFieldValue={setFieldValue}
        mode={contactModalMode}
        targetContact={contactToEdit}
      />
      <BillingContactModal
        isOpen={isBillingContactOpen}
        setIsOpen={setIsBillingContactOpen}
        setFieldValue={setFieldValue}
        mode={billingContactmodalMode}
        targetContact={billingContactToEdit}
      />
    </>
  );
};

// See https://github.com/globis-org/glopla-dev-portal/issues/122#issuecomment-417604558
const DisabledContactField = () => (
  <Select options={[]} placeholder="---" disabled={true} />
);

const Styled = styled.div`
  .l-block {
    margin-bottom: 3em;
  }

  .heading {
    display: flex;
    align-items: center;

    small {
      padding-left: 1em;
      color: #aaa;
    }
  }

  .next-button {
    text-align: center;
  }

  .hint {
    margin: 0.55em 0;
    color: #aaa;
    font-size: 0.9em;
    text-align: left;
  }

  ul.hint-separated {
    display: flex;
    margin: 0px;
    padding: 0px;
  }
  ul.hint-separated li {
    list-style: none;
    margin: 0px 0px 0.55em;
    padding: 0px;
    font-weight: bold;
  }
  ul.hint-separated li.item {
    white-space: nowrap;
    font-weight: bold;
  }
  ul.hint-separated li.hint {
    padding-left: 1em;
    color: #aaa;
    font-size: 0.9em;
    text-align: left;
  }
`;
